import { withTransaction } from '@elastic/apm-rum-react'
import {
  Button,
  ButtonBack,
  DakenService,
  Environment,
  Input,
  InputCountries,
  InputDocumentCpf,
  InputDocumentInternational,
  InputDocumentRut,
  InputName,
  InputPassword,
  JwtHelper,
  Loader,
  localForageHelper,
  LogService,
  MySquid,
  Steps,
  ToastHelper,
} from '@hub/shared'
import { ProfileAdditionalInfo } from '@hub/shared/interfaces/user.interface'
import { SqInputBirthdaySimple, SqSelector } from '@squidit/react-css'
import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'

import useDoLoginHook from '@src/hooks/use-do-login/use-do-login.hook'
import './register.page.scoped.scss'

export type StepStatus = 'verify' | 'personal-data' | 'password'

export interface ISteps {
  status: StepStatus
}

export const initialSteps: ISteps[] = [{ status: 'verify' }, { status: 'personal-data' }, { status: 'password' }]

export const styleSteps: React.CSSProperties = {
  backgroundColor: 'transparent',
  borderTop: 'none',
}

interface IValidation {
  email: boolean
  name: boolean
  nationality: boolean
  document: boolean
  birthdate: boolean
  socialName?: boolean
  password?: boolean
}

interface IRegister {
  email: string
  password: string
  name: string
  country: string
  nationality: string
  document: string
  documentType?: string
  birthdate: string
  passwordConfirmation: string
  isPersonAccount: number
  socialName?: string
}

interface RegisterPageProps extends React.HTMLAttributes<HTMLDivElement> {
  configs: MySquid | any
  environments: Environment
}

interface Payload extends ProfileAdditionalInfo {
  password: string
  token: string
}

const RegisterPageComponent = ({ configs, environments }: RegisterPageProps) => {
  const dakenService = useMemo(() => new DakenService(), [])
  const logService = useMemo(() => new LogService(), [])
  const toast = useMemo(() => new ToastHelper().toast, [])
  const navigate = useNavigate()
  const location = useLocation()
  logService.init('Register Page')
  const { t, i18n } = useTranslation('registerPage')
  const { search } = useLocation()
  const jwtHelper = useMemo(() => new JwtHelper(), [])

  const emailReceived = history?.state?.usr?.email || ''

  const [steps, setSteps] = useState<ISteps[]>(initialSteps)
  const [stepActive, setStepActive] = useState(initialSteps?.findIndex((step) => step.status === 'personal-data'))
  const [token, setToken] = useState<string | null>('')
  const [isTokenExpired, setIsTokenExpired] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingInfos, setLoadingInfos] = useState(true)
  const [hasEmail, setHasEmail] = useState(0)
  const [timeOuted, setTimeOuted] = useState(true)
  const [provider] = useState(history?.state?.usr?.provider ?? '')
  const [hasAcceptedContract, setHasAcceptedContract] = useState(false)
  const [validation, setValidation] = useState<IValidation>({
    name: false,
    email: true,
    document: false,
    nationality: true,
    birthdate: false,
    socialName: true,
  })
  const [register, setRegister] = useState<IRegister>({
    email: emailReceived,
    country: configs?.country || configs?.defaultCountry || 'BR',
    nationality: configs?.country || configs?.defaultCountry || 'BR',
    documentType: '',
    document: '',
    name: '',
    birthdate: '',
    password: '',
    passwordConfirmation: '',
    isPersonAccount: 1,
    socialName: '',
  })
  const [errors, setErrors] = useState<IRegister>({
    email: '',
    country: '',
    nationality: '',
    document: '',
    name: '',
    birthdate: '',
    password: '',
    passwordConfirmation: '',
    isPersonAccount: 0,
    socialName: '',
  })
  const { getUtms, removeUtms } = useDoLoginHook({ email: register?.email, password: register?.password })

  const getProfilePayload = useCallback(async (): Promise<ProfileAdditionalInfo> => {
    const query = new URLSearchParams(location.search)
    const utmMedium = query.get('utm_medium')
    const utmSource = query.get('utm_source')
    const utmCampaign = query.get('utm_campaign')
    const utmContent = query.get('utm_content')
    const adId = query.get('ad_id')
    const adName = query.get('ad_name')
    const birthday = register.birthdate
      ?.replace(/\D/g, '')
      ?.replace(/(\d{4})(\d{2})(\d{2})/, i18n?.language?.startsWith('en') ? '$1-$3-$2' : '$1-$2-$3')
    let documentHandler = ''
    if (register.nationality === 'BR') {
      documentHandler = 'CPF'
    } else if (register.nationality === 'CL') {
      documentHandler = 'RUT'
    } else {
      documentHandler = 'International'
    }

    const payload: Payload = {
      document: register.document.replace(/[^a-zA-Z0-9]/g, ''),
      name: register.name,
      email: register.email,
      isPersonAccount: register.isPersonAccount,
      birthday: birthday,
      documentType: documentHandler,
      country: register.country,
      nationality: register.nationality,
      password: register.password,
      registeredFromSource: utmSource ?? 'hub',
      registeredFromCampaign: utmCampaign ?? null,
      registeredFromMedium: utmMedium ?? null,
      registeredFromContent: utmContent ?? null,
      registeredFromAdId: adId ?? null,
      registeredFromAdName: adName ?? null,
      allowSMS: 1,
      allowWhatsapp: 1,
      allowBrandLovers: 1,
      allowAffiliates: 1,
      allowResearch: 1,
      allowSuggestionEmail: 1,
      language: '',
      lat: null,
      lng: null,
      token: token || '',
      socialName: register?.socialName || '',
    }

    let language = 'en'
    if (i18n.language && environments?.langs?.length) {
      for (var i = 0; i < environments.langs.length; i++) {
        if (i18n.language.startsWith(environments.langs[i])) {
          language = environments.langs[i]
          break
        }
      }
    }
    payload.language = language

    // Remove do payload as propriedades que não tem valor
    const removeKeys: any = []

    for (let value of Object.values(payload)) {
      if (!value) {
        removeKeys.push(value)
      }
    }

    for (let key in removeKeys) {
      delete (payload as any)[key]
    }

    return payload
  }, [
    environments.langs,
    i18n.language,
    register.birthdate,
    register.country,
    register.document,
    register.email,
    register.isPersonAccount,
    register.name,
    register.nationality,
    register.password,
    token,
    register?.socialName,
    location.search,
  ])

  const postDaken = useCallback(async (): Promise<ProfileAdditionalInfo> => {
    try {
      const profile = await getProfilePayload()
      const profileAdditionalInfos = await dakenService.insertAdditionalInfo(profile)
      await removeUtms()
      return profileAdditionalInfos
    } catch (error) {
      logService.error({ message: 'Failed to register', error, user: register })
      throw error
    }
  }, [dakenService, getProfilePayload, logService, register, removeUtms])

  const doRegister = useCallback(
    async (event: React.FormEvent): Promise<void> => {
      event.preventDefault()
      setLoading(true)
      try {
        await postDaken().then(() => {
          toast.success(t('messages.success'), {
            closeButton: true,
            className: 'full text-bold',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            duration: 10000,
            notOverClick: true,
            immediately: true,
          })
          navigate('/login')
        })
      } catch (error) {
        logService.error({ error, message: 'Failed to complete the registration process' })
        toast.error(t('messages.error'), {
          closeButton: true,
          className: 'full text-bold',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
      } finally {
        setLoading(false)
        navigate('/login')
      }
    },
    [logService, navigate, postDaken, t, toast]
  )

  const validate = (): boolean => {
    return Object.values(validation).every((element) => element)
  }

  const passwordConfirmationValidation = useCallback((password: string, confirmation: string): boolean => {
    return !!password && password === confirmation
  }, [])

  const handlePassword = (password: string) => {
    let error = ''
    if (!passwordConfirmationValidation(register.passwordConfirmation, password)) {
      error = t('newPasswordPage:differentPasswords')
    }
    setRegister((prevRegister) => ({
      ...prevRegister,
      password,
      passwordConfirmation: prevRegister.passwordConfirmation,
    }))
    setErrors((prevErrors) => ({ ...prevErrors, passwordConfirmation: register.passwordConfirmation === '' ? '' : error }))
    setValidation((prevValidation) => ({ ...prevValidation, passwordConfirmation: !error }))
  }

  const handleConfirmation = (confirmation: string): void => {
    let error = ''
    if (!confirmation || confirmation?.length < 1) {
      error = t('newPasswordPage:required')
    } else if (!passwordConfirmationValidation(register?.password, confirmation)) {
      error = t('newPasswordPage:differentPasswords')
    }
    setRegister((prevRegister) => ({ ...prevRegister, passwordConfirmation: confirmation }))
    setErrors((prevErrors) => ({ ...prevErrors, passwordConfirmation: error }))
    setValidation((prevValidation) => ({ ...prevValidation, passwordConfirmation: !error }))
  }

  const handleSocialName = (socialName: string) => {
    if (validateSocialName(socialName)) {
      setErrors((prevErrors) => ({ ...prevErrors, socialName: '' }))
      setValidation((prevValidation) => ({ ...prevValidation, socialName: true }))
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, socialName: t('socialNameError') }))
      setValidation((prevValidation) => ({ ...prevValidation, socialName: false }))
    }
    setRegister((prevRegister) => ({ ...prevRegister, socialName }))
  }

  const validateSocialName = useCallback((socialName: string) => {
    return /^[a-zA-ZÀ-ÿ\s]+$/g.test(socialName) || socialName === ''
  }, [])

  const handleContract = () => {
    setHasAcceptedContract((value) => !value)
  }

  useEffect(() => {
    const saveTokenOnStorage = async (token: string | null) => {
      if (token) {
        setToken(token)
        await localForageHelper?.setItem('token', token)
      }
    }

    try {
      const query = new URLSearchParams(search)
      const tokenParam = query.get('token')
      saveTokenOnStorage(tokenParam)

      // TODO: Verificar se o token é válido?
      if (!tokenParam) {
        navigate('/login')
      } else {
        const payload = jwtHelper.getPayloadToken(tokenParam)
        if (jwtHelper.verifyExpirationToken(tokenParam)) {
          setIsTokenExpired(true)
        }
        const email = payload?.email || ''
        if (email) {
          setLoadingInfos(false)
          setHasEmail(1)
          setRegister((prevRegister) => ({ ...prevRegister, email }))
        } else {
          setHasEmail(0)
        }
      }
    } catch (error) {
      setHasEmail(-1)
      logService.error({
        error,
        message: 'Error on Mount Register Page',
      })
    }
  }, [jwtHelper, logService, navigate, search])

  useEffect(() => {
    const includeUtms = async () => {
      const query = new URLSearchParams(search)
      const utms = await getUtms()
      query.set('utm_source', utms?.utm_source ?? '')
      query.set('utm_medium', utms?.utm_medium ?? '')
      query.set('utm_campaign', utms?.utm_campaign ?? '')
      query.set('utm_content', utms?.utm_content ?? '')
      query.set('ad_id', utms?.ad_id ?? '')
      query.set('ad_name', utms?.ad_name ?? '')
      navigate({ search: query.toString() })
    }

    includeUtms()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onTimeout = (timeOuted: boolean) => {
    setTimeOuted(timeOuted)
  }

  const handlePrevious = () => {
    switch (stepActive) {
      case 0:
        navigate('/login')
        break
      case 1:
        navigate('/login/verify')
        break
      case 2:
        setStepActive((stepActive) => stepActive - 1)
        break
      default:
        break
    }
  }

  const submitForm = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (provider) {
        doRegister(e)
      } else {
        setStepActive((stepActive) => stepActive + 1)
      }
    },
    [doRegister, provider]
  )

  const submitFormWithPassword = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      doRegister(e)
    },
    [doRegister]
  )

  const getPlaceholder = useCallback(() => {
    const language = i18n?.language || navigator?.language || ''
    return language.startsWith('en') ? 'MM/DD/YYYY' : 'DD/MM/YYYY'
  }, [i18n.language])

  if (hasEmail === -1) {
    return <Navigate to="/login/verify" />
  }

  return loading ? (
    <Loader />
  ) : (
    <div>
      <header className="register-header">
        <ButtonBack onClick={() => handlePrevious()} className="top-back-register" />
      </header>
      <h4>{t('title')}</h4>
      <Steps steps={steps} active={stepActive} style={styleSteps} />
      {stepActive === 1 ? (
        <form className={`${loadingInfos ? 'loading-form ' : ''}`} onSubmit={submitForm} style={{ width: '100%' }}>
          <Input
            label={t('globalLogin:email')}
            type="email"
            placeholder="email@email.com"
            value={register?.email}
            className="mb-3"
            name="register-email"
            id="register-email"
            onChange={(email: string) => setRegister((prevRegister) => ({ ...prevRegister, email }))}
            onValidate={(email: boolean) => setValidation((prevValidation) => ({ ...prevValidation, email }))}
            readOnly={true}
            autoComplete="email"
            required
          />
          <InputName
            label={t('name')}
            value={register?.name}
            className="mb-3"
            placeholder="Ex: John Doe"
            name="register-name"
            id="register-name"
            onChange={(name: string) => setRegister((prevRegister) => ({ ...prevRegister, name }))}
            onValidate={(name: boolean) => setValidation((prevValidation) => ({ ...prevValidation, name }))}
            readOnly={loading || loadingInfos}
            autoComplete="name"
            required
          />
          <Input
            label={t('socialName')}
            type="text"
            placeholder=""
            value={register?.socialName}
            className="mb-3"
            name="register-social-name"
            id="register-social-name"
            onChange={(socialName: string) => handleSocialName(socialName)}
            readOnly={loading || loadingInfos}
            externalError={errors?.socialName}
            maxLength={50}
          />
          <InputCountries
            label={t('nationality')}
            placeholder={t('nationalityPlaceholder')}
            value={register?.nationality}
            className="mb-3"
            name="register-nationality"
            id="register-nationality"
            loading={loadingInfos}
            onChange={(nationality: string) => setRegister((prevRegister) => ({ ...prevRegister, nationality }))}
            onValidate={(nationality: boolean) => setValidation((prevValidation) => ({ ...prevValidation, nationality }))}
            disabled={loading || loadingInfos}
            required
          />
          {register?.nationality === 'BR' && (
            <InputDocumentCpf
              className="mb-3"
              name="register-document"
              id="register-document"
              value={register?.document}
              label={'CPF'}
              onChange={(document: string) => setRegister((prevRegister) => ({ ...prevRegister, document }))}
              onValidate={(document: boolean) => setValidation((prevValidation) => ({ ...prevValidation, document }))}
              readOnly={loading || loadingInfos}
              disabled={loading || loadingInfos}
              isNew={true}
              required
            ></InputDocumentCpf>
          )}
          {register?.nationality === 'CL' && (
            <InputDocumentRut
              className="mb-3"
              name="register-document"
              id="register-document"
              value={register?.document}
              label={'RUT'}
              onChange={(document: string) => setRegister((prevRegister) => ({ ...prevRegister, document }))}
              onValidate={(document: boolean) => setValidation((prevValidation) => ({ ...prevValidation, document }))}
              readOnly={loading || loadingInfos}
              disabled={loading || loadingInfos}
              externalError={errors.document}
              isNew={true}
              required
            ></InputDocumentRut>
          )}
          {register?.nationality !== 'BR' && register.nationality !== 'CL' && (
            <InputDocumentInternational
              className="mb-3"
              name="register-document"
              id="register-document"
              value={register?.document}
              label={t('document')}
              nationality={register?.nationality}
              onChange={(document: string) => setRegister((prevRegister) => ({ ...prevRegister, document }))}
              onValidate={(document: boolean) => setValidation((prevValidation) => ({ ...prevValidation, document }))}
              readOnly={loading || loadingInfos}
              disabled={loading || loadingInfos}
              externalError={errors.document}
              isNew={true}
              onTimeout={onTimeout}
              loading={!timeOuted}
              required
            ></InputDocumentInternational>
          )}
          <SqInputBirthdaySimple
            label={t('birthdate')}
            value={register?.birthdate}
            name="register-birthdate"
            id="register-birthdate"
            placeholder={getPlaceholder()}
            onChange={(birthdate: string) => setRegister((prevRegister) => ({ ...prevRegister, birthdate }))}
            onValidate={(birthdate: boolean) => setValidation((prevValidation) => ({ ...prevValidation, birthdate }))}
            readOnly={loading || loadingInfos}
            autoComplete="birthdate"
            required={true}
            lang={i18n?.language || navigator.language}
          />
          <Button width="100%" size="xl" disabled={!timeOuted || !validate() || loadingInfos} type="submit">
            {provider ? t('globalLogin:register') : t('next')}
          </Button>
          <p className="mt-5">
            <span dangerouslySetInnerHTML={{ __html: t('alreadyRegistered') }} /> <Link to="/login">{t('clickHere')}</Link>
          </p>
        </form>
      ) : null}
      {stepActive === 2 ? (
        <form className={`${loadingInfos ? 'loading-form ' : ''}`} onSubmit={submitFormWithPassword} style={{ width: '100%' }}>
          <InputPassword
            label={t('globalLogin:password')}
            value={register?.password}
            name="register-password"
            id="register-password"
            className="mb-3"
            placeholder="********"
            onChange={handlePassword}
            onValidate={(password: boolean) => setValidation((prevValidation) => ({ ...prevValidation, password }))}
            readOnly={loading || loadingInfos}
            autoComplete="current-password"
            showValidation
            required
          />
          <InputPassword
            label={t('newPasswordPage:passwordConfirmation')}
            value={register?.passwordConfirmation}
            name="password-confirmation"
            id="password-confirmation"
            className="mb-3"
            placeholder="********"
            onChange={handleConfirmation}
            readOnly={loading || loadingInfos}
            externalError={errors?.passwordConfirmation}
            autoComplete="current-password"
            required
          />
          <SqSelector
            name="contract-acceptance"
            checked={hasAcceptedContract}
            onChange={handleContract}
            type="checkbox"
            wrapperClass="display-flex align-items-flex-start checkbox-contract-acceptance"
            rightLabel={<p style={{ fontSize: '0.9rem' }} dangerouslySetInnerHTML={{ __html: t('contract') }} />}
          />
          <Button
            loading={loading}
            type="submit"
            disabled={
              !timeOuted ||
              !passwordConfirmationValidation(register?.password, register?.passwordConfirmation) ||
              loadingInfos ||
              !hasAcceptedContract ||
              !validation.password
            }
            className="button-register"
            size="xl"
            id="button-register"
            width="100%"
          >
            {t('globalLogin:register')}
          </Button>
        </form>
      ) : null}
    </div>
  )
}

export default withTransaction('RegisterPageComponent', 'component')(RegisterPageComponent)
